import clsx from "clsx";
import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import UserSettingsService, { defaultDevicePageVisibleColumns } from "src/services/userSettingsService";
import { useDispatch, useSelector } from "src/store";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import QueryBuilder from "src/ui/shared/form/QueryBuilderField/QueryBuilderField.utils";
import { TableBody } from "src/ui/shared/table/TableBody";
import { makeStyles } from "tss-react/mui";

import { ManagementSection } from "@dashboard/devices/components/Table/ManagementSection";
import {
    selectBatchActionIsTypeMove,
    selectDevicePageExternalFilterUpdate,
    selectSearchQuery,
    updateSearchQuery,
} from "@dashboard/devices/store";
import { selectGroupsEntities } from "@dashboard/groups/store/selectors";
import { useLicenses } from "@dashboard/licenses/api/helpers";
import LicensesSelectors from "@dashboard/licenses/store/selectors";
import { useCurrentSubscription, useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { EmptyState } from "@shared/EmptyState/EmptyState";
import emptyDevicesImage from "@shared/images/EmptyDevices.webp";
import { LoadingBox, LoadingIcon } from "@shared/Loading";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { MinimalTable, MinimalTableContainer, tableRowHeight } from "@shared/table/MinimalTableComponents";
import CustomTablePagination from "@shared/table/TablePagination";
import { fuzzyFilter } from "@shared/table/Tanstack.utils";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import {
    ColumnFiltersState,
    ColumnOrderState,
    functionalUpdate,
    getCoreRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    OnChangeFn,
    SortingState,
    Updater,
    useReactTable,
    VisibilityState,
} from "@tanstack/react-table";

import { AddDeviceModal } from "./AddDeviceModal/AddDeviceModal";
import { useAddDevice } from "./AddDeviceModal/useAddDevice";
import { DeviceFilterType } from "./filters/DeviceTableFilters.utils";
import {
    applyStableOrder,
    DeviceTableData,
    getDeviceTableData,
    normalizeColumnOrder,
    StableOrderState,
    tableColumns,
} from "./DeviceTable.utils";
import { DeviceTableHeaders } from "./DeviceTableHeaders";
import { DeviceTableRow } from "./DeviceTableRow";
import { useColumnReorder } from "./useColumnDrag";

const useStyles = makeStyles()((theme) => ({
    loadingOverlay: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: tableRowHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(3),
        color: theme.palette.emteria.text.secondary,
        backgroundColor: theme.palette.emteria.background.paperDeep,
        borderTop: `1px solid ${theme.palette.emteria.border.secondary}`,
        pointerEvents: "none",
        zIndex: 3,
    },
}));

export const DeviceTable = () => {
    const [sorting, setSorting] = React.useState(UserSettingsService.getDevicePageSorting());
    const [visibleColumns, setVisibleColumns] = React.useState(UserSettingsService.getDevicePageVisibleColumns());
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        UserSettingsService.getDevicePageColumnFilters(),
    );
    const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(() =>
        normalizeColumnOrder(UserSettingsService.getDevicePageColumnOrder()),
    );

    const { classes } = useStyles();
    const { classes: layoutClasses } = useFullPageTableLayoutStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["pages", "common"]);
    const theme = useTheme();
    const { addDevice, modalProps: addDeviceModalProps } = useAddDevice();

    const searchQuery = useSelector(selectSearchQuery);

    const licenses = useLicenses();
    const groupsById = useSelector(selectGroupsEntities);
    const devicePageExternalFilterUpdate = useSelector(selectDevicePageExternalFilterUpdate);

    const selectIsBatchActionMove = useSelector(selectBatchActionIsTypeMove);
    const isExpired = useCurrentSubscriptionExpired();
    const { data: subscription } = useCurrentSubscription({});
    const isValid = !!subscription?.isValid;
    const licenseStatus = useSelector(LicensesSelectors.selectLicensesStatus);
    const licensesLoaded = licenseStatus === "fulfilled" || licenseStatus === "rejected";

    const seenOrderRef = React.useRef<StableOrderState>({ map: new Map(), next: 0 });

    const data = React.useMemo(() => {
        const rows = getDeviceTableData(licenses, groupsById, isExpired, theme, isValid);
        return applyStableOrder(rows, seenOrderRef.current);
    }, [licenses, groupsById, isExpired, isValid, theme]);

    const setFilters = (id: DeviceFilterType, values: Record<string, boolean>) => {
        setColumnFilters((prev) => {
            const prevFiltersWithoutId = prev.filter((filter) => filter.id !== id);
            const value = Object.keys(_.pickBy(values, (item) => item));

            if (!value.length) {
                UserSettingsService.setWorkspaceValue("devicePage.columnFilters", prevFiltersWithoutId);
                return prevFiltersWithoutId;
            }
            const newFilters = [{ id, value }, ...prevFiltersWithoutId];
            UserSettingsService.setWorkspaceValue("devicePage.columnFilters", newFilters);
            return newFilters;
        });
    };
    const setSearchQuery: OnChangeFn<unknown> = (value) => {
        if (typeof value === "string") {
            dispatch(updateSearchQuery(value));
        }
    };
    const handleSearchChange = (value: string) => {
        dispatch(updateSearchQuery(value));
    };
    const handleClearSearchQuery = () => {
        dispatch(updateSearchQuery(""));
    };
    const onSortingChange = (updater: Updater<SortingState>) => {
        const newValue = functionalUpdate(updater, sorting);
        setSorting(newValue);
        UserSettingsService.setWorkspaceValue("devicePage.sorting", newValue);
    };
    const onColumnVisibilityChange = (updater: Updater<VisibilityState>) => {
        const newValue = functionalUpdate(updater, visibleColumns);
        setVisibleColumns(newValue);
        UserSettingsService.setWorkspaceValue("devicePage.visibleColumns", newValue);
    };
    const onColumnOrderChange = (updater: Updater<ColumnOrderState>) => {
        const newValue = normalizeColumnOrder(functionalUpdate(updater, columnOrder));
        setColumnOrder(newValue);
        UserSettingsService.setWorkspaceValue("devicePage.columnOrder", newValue);
    };

    const handleResetFilters = () => {
        setColumnFilters([]);
        dispatch(updateSearchQuery(""));
        UserSettingsService.setWorkspaceValue("devicePage.columnFilters", []);
    };
    const handleResetColumns = () => {
        onColumnOrderChange([]);
        onColumnVisibilityChange(defaultDevicePageVisibleColumns);
        onSortingChange([]);
        setColumnFilters([]);
        UserSettingsService.setWorkspaceValue("devicePage.columnFilters", []);
    };

    const table = useReactTable({
        data: data,
        columns: tableColumns,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        state: {
            globalFilter: searchQuery,
            columnVisibility: {
                ...visibleColumns,
                moveLicenseCheckbox: selectIsBatchActionMove,
            },
            columnFilters,
            columnOrder,
            sorting,
        },
        initialState: {
            pagination: {
                pageSize: UserSettingsService.getDevicePageRowsPerPage(),
                pageIndex: 0,
            },
        },
        defaultColumn: { size: 0 },
        autoResetPageIndex: false,
        getColumnCanGlobalFilter: () => true,
        onColumnVisibilityChange,
        onColumnOrderChange,
        onGlobalFilterChange: setSearchQuery,
        onSortingChange: onSortingChange,
        globalFilterFn: QueryBuilder.filterByQueryMap<DeviceTableData>,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    const tableRows = table.getPaginationRowModel().rows;
    const totalRows = table.getFilteredRowModel().rows.length;
    const currentPageIndex = table.getState().pagination.pageIndex;
    const pageSize = table.getState().pagination.pageSize;

    const isEmpty = tableRows.length === 0 || tableRows.every((row) => row.getVisibleCells().length === 0);

    // Match the files/downloads tables: the body always fills the available viewport height and
    // scrolls internally only when the rows overflow it. A short page just shows the plain table
    // background below the last row — no synthetic filler rows — and the pagination stays pinned
    // to the bottom.
    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [
        currentPageIndex,
        pageSize,
        tableRows.length,
        isEmpty,
        licenses.length,
        visibleColumns,
    ]);
    const [scrollContainer, setScrollContainer] = React.useState<HTMLDivElement | null>(null);
    const scrollContainerRef = React.useCallback(
        (container: HTMLDivElement | null) => {
            setScrollContainer(container);
            return tableContainerRef(container);
        },
        [tableContainerRef],
    );
    useColumnReorder(table, scrollContainer);

    const filterActive = !_.isEmpty(columnFilters) || !_.isEmpty(searchQuery);
    // A license is listed as a row before any device is enrolled with it, so count devices rather than rows.
    const hasNoDevices = licenseStatus === "fulfilled" && !licenses.some((license) => license.device);

    React.useEffect(() => {
        table.setPageIndex(0);
    }, [columnFilters, searchQuery, sorting, table]);

    React.useEffect(() => {
        if (devicePageExternalFilterUpdate > 0) {
            setColumnFilters(UserSettingsService.getDevicePageColumnFilters());
            setVisibleColumns(UserSettingsService.getDevicePageVisibleColumns() ?? {});
        }
    }, [devicePageExternalFilterUpdate]);

    return (
        <Box className={layoutClasses.tableLayout}>
            {hasNoDevices ? (
                <EmptyState
                    framed
                    image={emptyDevicesImage}
                    title={t("pages:devicePage.emptyState.title")}
                    description={t("pages:devicePage.emptyState.description")}
                    action={
                        <PrimaryButton onClick={addDevice} name="enroll-first-device">
                            {t("common:button.enrollFirstDevice")}
                        </PrimaryButton>
                    }
                />
            ) : (
                <>
                    <ManagementSection
                        onAddDevice={addDevice}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onClear={handleClearSearchQuery}
                        onResetColumns={handleResetColumns}
                        table={table}
                    />
                    <Box className={layoutClasses.tableWrapper}>
                        <MinimalTableContainer
                            ref={scrollContainerRef}
                            className={clsx(
                                layoutClasses.tableContainer,
                                !contentOverflows && layoutClasses.tableContainerNoScroll,
                            )}
                        >
                            <MinimalTable>
                                <DeviceTableHeaders table={table} columnFilters={columnFilters} setFilters={setFilters} />

                                <TableBody
                                    table={table}
                                    filter={{
                                        isActive: filterActive,
                                        onResetFilter: handleResetFilters,
                                    }}
                                    loading={tableRows.length > 0 && licenseStatus === "pending"}
                                    noDataMessage={licensesLoaded ? t("common:table.noDevices") : <LoadingBox />}
                                >
                                    {tableRows.map((row) => (
                                        <DeviceTableRow key={row.id} {...row} />
                                    ))}
                                </TableBody>
                            </MinimalTable>
                        </MinimalTableContainer>
                        {tableRows.length > 0 && (licenseStatus === "pending" || licenseStatus === "updating") ? (
                            <Box className={classes.loadingOverlay}>
                                <LoadingIcon />
                                <Typography>{t("pages:overviewPage.tooltips.loadingMoreData")}</Typography>
                            </Box>
                        ) : null}
                    </Box>
                </>
            )}
            {licensesLoaded && !hasNoDevices ? (
                <CustomTablePagination
                    component="div"
                    className={layoutClasses.pagination}
                    rowsPerPageOptions={[12, 24, 48]}
                    count={totalRows}
                    rowsPerPage={pageSize}
                    page={currentPageIndex}
                    onPageChange={(_e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
                        table.setPageIndex(newPage);
                    }}
                    onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newSize = parseInt(e.target.value, 10);
                        table.setPageSize(newSize);
                        table.setPageIndex(0);
                        UserSettingsService.setWorkspaceValue("devicePage.rowsPerPage", newSize);
                    }}
                    slotProps={{
                        select: {
                            name: "rows-per-page",
                        },
                    }}
                />
            ) : null}
            <AddDeviceModal {...addDeviceModalProps} />
        </Box>
    );
};
